import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  PencilSimple,
  Trash,
  Camera,
  Package,
  Printer,
  EnvelopeSimple,
  QrCode,
  Truck,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AppHeader } from "@/components/AppHeader";
import { PhotoGallery } from "@/components/PhotoGallery";
import { DateField } from "@/components/DateField";
import { RMAEmailDialog } from "@/components/RMAEmailDialog";
import { api, formatApiErrorDetail, downloadFile } from "@/lib/api";
import {
  RMA_STATUSES,
  RMA_STATUS_STYLE,
  rmaMoney,
  rmaDate,
} from "@/pages/RMAs";

// statuses that take the items out of inventory on the backend (one time)
const SHIP_STATUSES = new Set(["Shipped", "Credited", "Replaced", "Refunded"]);
const KIND_LABEL = { slab: "Slab", remnant: "Remnant", sink: "Sink", other: "Other" };

const prettyDate = (ymd) =>
  ymd
    ? new Date(`${ymd.slice(0, 10)}T12:00:00`).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "";

export default function RMADetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const scanned = params.get("scan") === "1";
  const [rma, setRma] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reply, setReply] = useState({ pickup_date: "", credit_note: "" });
  const [emailOpen, setEmailOpen] = useState(false);
  const [supplier, setSupplier] = useState(null);
  const photoInput = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/rmas/${id}`);
      setRma(data);
      setReply({ pickup_date: data.pickup_date || "", credit_note: data.credit_note || "" });
    } catch {
      setRma(null);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const sid = rma?.supplier_id;
    if (!sid) {
      setSupplier(null);
      return;
    }
    api.get(`/suppliers/${sid}`).then(({ data }) => setSupplier(data)).catch(() => setSupplier(null));
  }, [rma?.supplier_id]);

  const dismissScan = () => {
    params.delete("scan");
    setParams(params, { replace: true });
  };

  const saveReply = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/rmas/${id}/supplier-reply`, reply);
      setRma(data);
      toast.success("Supplier reply saved — it prints on the label");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
    }
    setBusy(false);
  };

  const setStatus = async (status, { confirmed = false } = {}) => {
    if (
      !confirmed &&
      SHIP_STATUSES.has(status) &&
      !rma.stock_removed &&
      (rma.items || []).some((it) => it.kind !== "other" && it.item_id)
    ) {
      const ok = window.confirm(
        `Mark this return "${status}"? The items will be taken out of inventory now (they're going back to the supplier).`,
      );
      if (!ok) return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/rmas/${id}/status`, { status });
      setRma(data);
      if (status === "Approved" && data.email) {
        if (data.email.sent) toast.success(`Approved — return request emailed to ${data.email.to}`);
        else toast.message("Approved", { description: `Supplier not emailed: ${data.email.reason}` });
      } else {
        toast.success(`Return marked ${status}`);
      }
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not update status",
      );
    }
    setBusy(false);
  };

  const uploadPhotos = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    if (picked.length === 0) return;
    const fd = new FormData();
    picked.forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post(`/rmas/${id}/photos`, fd);
      setRma(data);
      toast.success(picked.length === 1 ? "Photo added" : `${picked.length} photos added`);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Photo upload failed",
      );
    }
  };

  const removePhoto = async (photoId) => {
    const { data } = await api.delete(`/rmas/${id}/photos/${photoId}`);
    setRma(data);
    toast.success("Photo removed");
  };

  const printLabels = async (item) => {
    try {
      const suffix = item == null ? "" : `?item=${item}`;
      await downloadFile(`/rmas/${id}/labels.pdf${suffix}`,
        `${rma.code}${item == null ? "" : `-item${item + 1}`}-label-4x6.pdf`);
    } catch {
      toast.error("Could not build the label");
    }
  };

  const emailSupplier = () => setEmailOpen(true);

  const remove = async () => {
    if (!window.confirm("Delete this return? This cannot be undone.")) return;
    await api.delete(`/rmas/${id}`);
    toast.success("Return deleted");
    navigate("/inventory/rmas");
  };

  if (!rma)
    return (
      <div className="grain min-h-screen">
        <AppHeader title="Inventory / Return" />
        <main className="relative z-10 pt-6" data-testid="rma-detail-loading">
          <p className="text-sm text-zinc-500">Loading…</p>
        </main>
      </div>
    );

  const galleryPhotos = (rma.photos || []).map((p) => ({
    id: p.id,
    storage_path: p.path,
    original_filename: "Return photo",
  }));

  const facts = [
    ["Supplier", rma.supplier || "No supplier"],
    ["Supplier RMA #", rma.manufacturer_rma],
    ["Supplier PO / invoice", rma.po_number],
    ["Pickup date", prettyDate(rma.pickup_date)],
    ["Credit note #", rma.credit_note],
    ["Resolution", rma.resolution],
    rma.credit_amount != null
      ? ["Credit / refund", rmaMoney(rma.credit_amount)]
      : null,
    ["Out of inventory", rma.stock_removed ? "Yes" : "Not yet"],
    ["Supplier emailed", rma.supplier_notified_at
      ? `${rmaDate(rma.supplier_notified_at)} · ${rma.supplier_notified_to}`
      : ""],
    ["Shipped", rmaDate(rma.shipped_at)],
    ["Closed", rmaDate(rma.resolved_at)],
  ]
    .filter(Boolean)
    .filter(([, v]) => v);

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Inventory / Return" />
      <main className="relative z-10 pb-28 pt-6" data-testid="rma-detail">
        <Link
          to="/inventory/rmas"
          data-testid="back-to-rmas"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 hover:text-white"
        >
          <ArrowLeft size={14} weight="bold" /> All returns
        </Link>

        {scanned && (
          <div
            data-testid="rma-scan-banner"
            className={`mt-3 flex flex-wrap items-center gap-3 rounded-lg border p-4 ${
              rma.stock_removed
                ? "border-cyan-400/30 bg-cyan-500/10"
                : "border-yellow-500/40 bg-yellow-500/10"
            }`}
          >
            <QrCode size={22} weight="bold" className={rma.stock_removed ? "text-cyan-300" : "text-yellow-400"} />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-bold">
                {rma.stock_removed
                  ? `Already shipped ${rmaDate(rma.shipped_at)} — nothing left to do`
                  : `Label scanned · ${(rma.items || []).length} item${(rma.items || []).length === 1 ? "" : "s"} for ${rma.supplier || "the supplier"}`}
              </div>
              {!rma.stock_removed && (
                <div className="text-xs text-zinc-400">
                  Marking it shipped takes the pieces out of inventory.
                </div>
              )}
            </div>
            {!rma.stock_removed && (
              <Button
                data-testid="rma-scan-ship-button"
                disabled={busy}
                onClick={() => setStatus("Shipped", { confirmed: true })}
                className="h-12 bg-yellow-500 px-5 text-xs font-bold uppercase tracking-widest text-zinc-950 hover:bg-yellow-400"
              >
                <Truck size={16} weight="bold" className="mr-2" /> Mark shipped
              </Button>
            )}
            <button
              type="button"
              data-testid="rma-scan-dismiss"
              onClick={dismissScan}
              className="text-[11px] uppercase tracking-widest text-zinc-400 hover:text-white"
            >
              Dismiss
            </button>
          </div>
        )}

        <div className="mt-3 rounded-lg border border-zinc-800 bg-zinc-900 p-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono-data text-sm text-yellow-500">
                  {rma.code}
                </span>
                <span
                  data-testid="rma-detail-status"
                  className={`rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ${
                    RMA_STATUS_STYLE[rma.status] ||
                    "border-zinc-700 bg-zinc-800 text-zinc-300"
                  }`}
                >
                  {rma.status}
                </span>
                {rma.stock_removed && (
                  <span className="rounded-full border border-cyan-500/25 bg-cyan-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-cyan-300">
                    Out of inventory
                  </span>
                )}
              </div>
              <h1 className="mt-1 text-2xl font-bold tracking-tight sm:text-3xl">
                {rma.supplier || "No supplier"}
              </h1>
              <p className="mt-1 text-xs uppercase tracking-widest text-zinc-500">
                Opened {rmaDate(rma.created_at)}
                {rma.created_by ? ` · ${rma.created_by}` : ""}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                data-testid="print-rma-labels-button"
                onClick={() => printLabels(null)}
                className="h-11 bg-yellow-500 text-[11px] font-bold uppercase tracking-widest text-zinc-950 hover:bg-yellow-400"
              >
                <Printer size={14} weight="bold" className="mr-1.5" /> Print 4×6 labels
              </Button>
              <Button
                data-testid="email-supplier-button"
                variant="outline"
                disabled={busy}
                onClick={emailSupplier}
                className="h-11 border-zinc-700 bg-transparent text-[11px] font-bold uppercase tracking-widest text-zinc-300 hover:bg-zinc-800"
              >
                <EnvelopeSimple size={14} weight="bold" className="mr-1.5" />
                {rma.supplier_notified_at ? "Re-send to supplier" : "Email supplier"}
              </Button>
              <Button
                data-testid="edit-rma-button"
                variant="outline"
                onClick={() => navigate(`/inventory/rmas/${id}/edit`)}
                className="h-11 border-zinc-700 bg-transparent text-[11px] uppercase tracking-widest"
              >
                <PencilSimple size={14} weight="bold" className="mr-1.5" /> Edit
              </Button>
              <Button
                data-testid="delete-rma-button"
                variant="outline"
                onClick={remove}
                className="h-11 border-zinc-700 bg-transparent text-[11px] uppercase tracking-widest text-red-400 hover:text-red-300"
              >
                <Trash size={14} weight="bold" />
              </Button>
            </div>
          </div>

          <div className="mt-5 border-t border-zinc-800 pt-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">
              Update status
            </div>
            <div className="mt-2 flex flex-wrap gap-2" data-testid="rma-status-buttons">
              {RMA_STATUSES.map((s) => (
                <button
                  key={s}
                  type="button"
                  data-testid={`rma-set-status-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  disabled={busy || s === rma.status}
                  onClick={() => setStatus(s)}
                  className={`h-9 rounded-full border px-3 text-[11px] font-bold uppercase tracking-widest transition-colors duration-150 disabled:opacity-40 ${
                    s === rma.status
                      ? RMA_STATUS_STYLE[s]
                      : "border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-white"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                Items going back to the supplier
              </div>
              {(rma.items || []).length === 0 ? (
                <p className="mt-3 text-sm text-zinc-500">No items recorded.</p>
              ) : (
                <div className="mt-3 space-y-2" data-testid="rma-items">
                  {rma.items.map((it, n) => (
                    <div
                      key={n}
                      data-testid={`rma-item-${n}`}
                      className="flex items-center gap-3 rounded-md border border-zinc-800 bg-zinc-950 p-3"
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-zinc-800 text-zinc-400">
                        <Package size={16} weight="bold" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-semibold">
                          {it.label || "Item"}
                        </div>
                        <div className="text-xs text-zinc-500">
                          {KIND_LABEL[it.kind] || it.kind}
                          {it.code ? ` · ${it.code}` : ""}
                          {[it.thickness, it.size, it.lot_number && `Lot ${it.lot_number}`]
                            .filter(Boolean)
                            .map((x) => ` · ${x}`)
                            .join("")}
                        </div>
                      </div>
                      <span className="font-mono-data text-sm text-zinc-300">
                        × {it.quantity || 1}
                      </span>
                      <button
                        type="button"
                        data-testid={`rma-item-label-${n}`}
                        onClick={() => printLabels(n)}
                        title="Print a 4×6 label for this item"
                        className="flex h-9 w-9 items-center justify-center rounded-md border border-zinc-800 text-zinc-400 transition-colors duration-150 hover:border-yellow-500/50 hover:text-yellow-400"
                      >
                        <Printer size={15} weight="bold" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {rma.reason && (
              <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                  Reason
                </div>
                <p className="mt-2 text-sm text-zinc-200">{rma.reason}</p>
              </div>
            )}
            {rma.notes && (
              <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                  Internal notes
                </div>
                <p className="mt-2 whitespace-pre-wrap text-sm text-zinc-200">
                  {rma.notes}
                </p>
              </div>
            )}

            <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                  Photos ({galleryPhotos.length})
                </div>
                <button
                  type="button"
                  data-testid="rma-add-photos"
                  onClick={() => photoInput.current?.click()}
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-yellow-400 hover:underline"
                >
                  <Camera size={13} weight="bold" /> Add photos
                </button>
                <input
                  ref={photoInput}
                  data-testid="rma-photo-input"
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={uploadPhotos}
                />
              </div>
              <PhotoGallery
                photos={galleryPhotos}
                onRemove={removePhoto}
                testPrefix="rma-photo"
              />
            </div>
          </div>

          <div className="space-y-6">
            <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4" data-testid="rma-supplier-reply">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                Supplier reply
              </div>
              <p className="mt-1 text-xs text-zinc-500">
                What the supplier came back with — both print on the 4×6 label.
              </p>
              <div className="mt-3 space-y-3">
                <div className="space-y-1">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Pickup date</div>
                  <DateField
                    testid="rma-reply-pickup-input"
                    value={reply.pickup_date}
                    onChange={(d) => setReply({ ...reply, pickup_date: d })}
                    placeholder="Pick a date"
                    className="mt-0"
                  />
                </div>
                <div className="space-y-1">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Credit note #</div>
                  <Input
                    data-testid="rma-reply-credit-note-input"
                    value={reply.credit_note}
                    onChange={(e) => setReply({ ...reply, credit_note: e.target.value })}
                    placeholder="CN-40921"
                    className="h-11 border-zinc-800 bg-zinc-950"
                  />
                </div>
                <Button
                  data-testid="rma-reply-save"
                  disabled={
                    busy ||
                    (reply.pickup_date === (rma.pickup_date || "") &&
                      reply.credit_note === (rma.credit_note || ""))
                  }
                  onClick={saveReply}
                  className="h-10 w-full bg-indigo-500 text-[11px] font-bold uppercase tracking-widest text-white hover:bg-indigo-400 disabled:opacity-40"
                >
                  Save supplier reply
                </Button>
              </div>
            </div>

            <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                Details
              </div>
              <dl className="mt-3 space-y-3" data-testid="rma-facts">
                {facts.map(([k, v]) => (
                  <div key={k}>
                    <dt className="text-[10px] uppercase tracking-widest text-zinc-500">
                      {k}
                    </dt>
                    <dd className="mt-0.5 text-sm text-zinc-200">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>
        {rma && (
          <RMAEmailDialog
            rma={rma}
            supplier={supplier}
            open={emailOpen}
            onClose={() => setEmailOpen(false)}
            onSent={(data) => data && setRma(data)}
          />
        )}
      </main>
    </div>
  );
}
